import api from './api';

/**
 * Serviço para gerenciar salas
 */
class SalasService {
  /**
   * Lista todas as salas
   * @returns {Promise<Array>}
   */
  async findAll() {
    return api.get('/salas');
  }

  /**
   * Lista salas ativas
   * @returns {Promise<Array>}
   */
  async findAllActive() {
    return api.get('/salas/ativas');
  }

  /**
   * Busca salas ativas por cliente
   * @param {number} clienteId
   * @returns {Promise<Array>}
   */
  async findActiveByCliente(clienteId) {
    return api.get(`/salas/ativas/cliente/${clienteId}`);
  }

  /**
   * Busca uma sala por ID
   * @param {number} id 
   * @returns {Promise<Object>}
   */
  async findOne(id) {
    return api.get(`/salas/${id}`);
  }

  /**
   * Busca salas por cliente
   * @param {number} clienteId 
   * @returns {Promise<Array>}
   */
  async findByCliente(clienteId) {
    return api.get(`/salas/cliente/${clienteId}`);
  }

  /**
   * Busca uma sala por nome
   * @param {string} nome 
   * @returns {Promise<Object>}
   */
  async findByNome(nome) {
    return api.get(`/salas/nome/${nome}`);
  }

  /**
   * Cria uma nova sala
   * @param {Object} createSalaDto 
   * @returns {Promise<Object>}
   */
  async create(createSalaDto) {
    return api.post('/salas', createSalaDto);
  }

  /**
   * Atualiza uma sala
   * @param {number} id 
   * @param {Object} updateSalaDto 
   * @returns {Promise<Object>}
   */
  async update(id, updateSalaDto) {
    return api.put(`/salas/${id}`, updateSalaDto);
  }

  /**
   * Remove uma sala
   * @param {number} id 
   * @returns {Promise<void>}
   */
  async remove(id) {
    return api.delete(`/salas/${id}`);
  }

  /**
   * Desativa uma sala
   * @param {number} id 
   * @returns {Promise<Object>}
   */
  async deactivate(id) {
    return api.put(`/salas/${id}/desativar`, {});
  }

  /**
   * Atualiza presença do primário na sala
   * @param {number} id 
   * @param {string} status 
   * @returns {Promise<Object>}
   */
  async updatePrimarioNaSala(id, status) {
    return api.put(`/salas/${id}/primario/${status}`);
  }

  /**
   * Atualiza presença do secundário na sala
   * @param {number} id 
   * @param {string} status 
   * @returns {Promise<Object>}
   */
  async updateSecundarioNaSala(id, status) {
    return api.put(`/salas/${id}/secundario/${status}`);
  }

  /**
   * Registra dados de qualidade de conexão da sala
   * @param {Object} createDto 
   * @returns {Promise<Object>}
   */
  async createQualidadeConexao(createDto) {
    return api.post('/salas/qualidade-conexao', createDto);
  }

  /**
   * Verifica capacidade da sala
   * @param {string} nome 
   * @returns {Promise<Object>}
   */
  async checkRoomCapacity(nome) {
    return api.get(`/salas/capacidade/${nome}`);
  }

  /**
   * Verifica status da sala pelos UIDs dos participantes
   * @param {string} primaryUID 
   * @param {string} secondaryUID 
   * @returns {Promise<Object>}
   */
  async getStatusByUIDs(primaryUID, secondaryUID) {
    return api.get(`/salas/status-uids/${primaryUID}/${secondaryUID}`);
  }

  /**
   * Atualiza presença de um UID da sala
   * @param {number} roomId 
   * @param {string} uid 
   * @returns {Promise<Object>}
   */
  async updateUIDFromRoom(roomId, uid) {
    return api.put(`/salas/${roomId}/uid/${uid}`);
  }

  /**
   * Busca informações de qualidade de conexão por UID
   * @param {string} uid 
   * @returns {Promise<Array>}
   */
  async getInfoByUID(uid) {
    return api.get(`/salas/uid/${uid}`);
  }

  /**
   * Busca logs de uma sala por ID
   * @param {number} salaId 
   * @returns {Promise<Array>}
   */
  async getLogsBySala(salaId) {
    return api.get(`/salas/${salaId}/logs`);
  }

  /**
   * Busca auditoria de uma sala por ID
   * @param {number} salaId 
   * @returns {Promise<Array>}
   */
  async getAuditoriaBySala(salaId) {
    return api.get(`/salas/${salaId}/auditoria`);
  }

  /**
   * Persiste tempos acumulados da chamada na sala.
   * @param {number} salaId
   * @param {Object} timesData
   * @returns {Promise<Object>}
   */
  async updateRoomTimes(salaId, timesData) {
    return api.put(`/salas/${salaId}/tempos`, timesData);
  }
}

export default new SalasService();
